import { SalesforceStage } from './client'
import { UserRole, userRoleGroups } from './user'

/**
 * A deal is one attempt to win a company: it opens at Qualifying, moves down the
 * ladder (BD through Term Sheet, UW from Due Diligence) and closes exactly once.
 * A Salesforce Lead and the Opportunity it converts into are the same deal. Between
 * deals a company sits in the Radar pool (see companyLifecycle.ts).
 */
export enum DealStage {
  Qualifying = 'Qualifying',
  Ops = 'OPS',
  Committee = 'Committee',
  TermSheet = 'Term Sheet',
  DueDiligence = 'Due Diligence',
  Contract = 'Contract',
  PreFunding = 'Pre-Funding',
}

/** Forward order. Index is the comparison key for "advanced past". */
export const DEAL_STAGE_LADDER: DealStage[] = [
  DealStage.Qualifying,
  DealStage.Ops,
  DealStage.Committee,
  DealStage.TermSheet,
  DealStage.DueDiligence,
  DealStage.Contract,
  DealStage.PreFunding,
]

export const dealStageIndex = (stage: DealStage): number => DEAL_STAGE_LADDER.indexOf(stage)

export const isDealStage = (value: string | null | undefined): value is DealStage =>
  !!value && (DEAL_STAGE_LADDER as string[]).includes(value)

export const isDiligenceStage = (stage: DealStage): boolean =>
  dealStageIndex(stage) >= dealStageIndex(DealStage.DueDiligence)

/** BD moves deals through Term Sheet and UW from Due Diligence on; admins and UW can do both. */
export const canMoveDealStage = (role: UserRole | null | undefined, stage: DealStage): boolean =>
  !!role && (isDiligenceStage(stage) ? userRoleGroups.UW_USERS : userRoleGroups.BDOS).includes(role)

/** BD owns Qualifying through Term Sheet; UW owns Due Diligence onward. */
export const BD_STAGES: DealStage[] = DEAL_STAGE_LADDER.filter((stage) => !isDiligenceStage(stage))
export const DILIGENCE_STAGES: DealStage[] = DEAL_STAGE_LADDER.filter(isDiligenceStage)

export enum DealOutcome {
  Funded = 'Funded',
  /** Never going to be a deal. */
  Inactive = 'Inactive',
  /** They declined, or didn't choose us. */
  Lost = 'Lost',
  /** Not now: credit, size or timing. */
  Passed = 'Passed',
}

export type DealCloseOutcome = Exclude<DealOutcome, DealOutcome.Funded>

/**
 * Closed Lost and Passed: the company didn't take our deal (yet). They count against the win
 * rate and carry Lost Deal Notes; an Inactive deal never was one.
 */
export const LOST_DEAL_OUTCOMES: DealOutcome[] = [DealOutcome.Lost, DealOutcome.Passed]

export const isLostDealOutcome = (outcome: DealOutcome | null | undefined): boolean =>
  !!outcome && LOST_DEAL_OUTCOMES.includes(outcome)

export const LOST_DEAL_NOTES_MAX_LENGTH = 20000

export enum DealCloseReason {
  Industry = 'Industry',
  Defunct = 'Defunct',
  Graduated = 'Graduated',
  Competitor = 'Competitor',
  NoDecision = 'No decision',
  Credit = 'Credit',
  TooSmall = 'Too small',
  Timing = 'Timing',
  Other = 'Other',
}

export const CLOSE_REASONS_BY_OUTCOME: Record<DealCloseOutcome, DealCloseReason[]> = {
  [DealOutcome.Inactive]: [
    DealCloseReason.Industry,
    DealCloseReason.Defunct,
    DealCloseReason.Graduated,
    DealCloseReason.Other,
  ],
  [DealOutcome.Lost]: [
    DealCloseReason.Competitor,
    DealCloseReason.NoDecision,
    DealCloseReason.Other,
  ],
  [DealOutcome.Passed]: [
    DealCloseReason.Credit,
    DealCloseReason.TooSmall,
    DealCloseReason.Timing,
    DealCloseReason.Other,
  ],
}

export const DEAL_CLOSE_OUTCOMES = Object.keys(CLOSE_REASONS_BY_OUTCOME) as DealCloseOutcome[]

/**
 * Bulk Lead sources (list buys, scrapers, trade shows). Their Leads stay unassigned on the
 * Radar, so Atlas never opens a deal for one. Matched as case-insensitive substrings.
 */
export const BULK_LEAD_SOURCES = [
  'Prospectr',
  'List Purchased',
  'Jigsaw',
  'Direct - Scraper',
  'Direct - Trade Show',
  'Direct - Zoominfo',
]

export const isBulkLeadSource = (leadSource: string | null | undefined): boolean => {
  const source = leadSource?.toLowerCase()
  return !!source && BULK_LEAD_SOURCES.some((bulk) => source.includes(bulk.toLowerCase()))
}

/** How a close reads in a deal's history: "Reason: note", either part optional. */
export const dealCloseEventNote = (
  reason: string | null | undefined,
  note: string | null | undefined,
): string | null => [reason, note].filter(Boolean).join(': ') || null

/** "Lost · Competitor": how a closed deal reads on a pill. */
export const dealOutcomeLabel = (
  outcome: DealOutcome | null | undefined,
  closeReason: string | null | undefined,
): string => [outcome, closeReason].filter(Boolean).join(' · ')

export const isReasonValidFor = (outcome: DealOutcome, reason: DealCloseReason | null): boolean =>
  outcome === DealOutcome.Funded
    ? reason === null
    : !!reason && CLOSE_REASONS_BY_OUTCOME[outcome].includes(reason)

export const isCloseNoteRequired = (reason: DealCloseReason | null): boolean =>
  reason === DealCloseReason.Other

/** What closing a deal decides for its company. */
export enum DealNextStep {
  Tracking = 'Tracking',
  Rejected = 'Rejected',
  Defunct = 'Defunct',
}

/** The next steps a BD may pick for a close, the suggested one first. Empty for Funded. */
export const nextStepsFor = (
  outcome: DealOutcome,
  reason: DealCloseReason | null,
): DealNextStep[] => {
  if (outcome === DealOutcome.Funded) return []
  if (reason === DealCloseReason.Defunct) return [DealNextStep.Defunct]
  if (outcome === DealOutcome.Inactive) return [DealNextStep.Rejected, DealNextStep.Tracking]
  if (outcome === DealOutcome.Lost) return [DealNextStep.Tracking, DealNextStep.Rejected]
  return [DealNextStep.Tracking]
}

export const suggestedNextStep = (
  outcome: DealOutcome,
  reason: DealCloseReason | null,
): DealNextStep | null => nextStepsFor(outcome, reason)[0] ?? null

/** Default months until a Tracking company is revisited; the BD can always change the date. */
export const defaultRevisitMonths = (
  outcome: DealOutcome,
  reason: DealCloseReason | null,
): number => {
  if (outcome === DealOutcome.Lost) return reason === DealCloseReason.Competitor ? 18 : 12
  if (reason === DealCloseReason.TooSmall) return 12
  return 6
}

export enum DealEventType {
  Created = 'created',
  StageChanged = 'stage_changed',
  Closed = 'closed',
  /** A close undone by Restore; the deal is open again where it closed. */
  Reopened = 'reopened',
  Deleted = 'deleted',
}

/* ------------------------------------------------------------------------------------------ */
/* Salesforce translation. Salesforce splits a deal across Lead.Status (Qualifying) and        */
/* Opportunity.StageName (OPS onward); these tables are removed at cutover.                    */
/* ------------------------------------------------------------------------------------------ */

export type SalesforceDealMapping =
  | { kind: 'stage'; stage: DealStage }
  | { kind: 'closed'; outcome: DealOutcome; reason: DealCloseReason | null }

const stage = (value: DealStage): SalesforceDealMapping => ({ kind: 'stage', stage: value })
const closed = (outcome: DealOutcome, reason: DealCloseReason | null): SalesforceDealMapping => ({
  kind: 'closed',
  outcome,
  reason,
})

/** Read direction. `Convert` and `Closed` are absent: they are lead-conversion plumbing. */
const SALESFORCE_STAGE_TO_DEAL: Record<string, SalesforceDealMapping> = {
  [SalesforceStage.IntroOrMention]: stage(DealStage.Qualifying),
  [SalesforceStage.QualifyingCall]: stage(DealStage.Qualifying),
  [SalesforceStage.GatheringInfo]: stage(DealStage.Qualifying),
  [SalesforceStage.OpsCall]: stage(DealStage.Ops),
  [SalesforceStage.Submission]: stage(DealStage.Committee),
  [SalesforceStage.TermSheetNegotiation]: stage(DealStage.TermSheet),
  [SalesforceStage.DueDiligence]: stage(DealStage.DueDiligence),
  [SalesforceStage.ContractNegotiation]: stage(DealStage.Contract),
  [SalesforceStage.Funding]: stage(DealStage.PreFunding),
  [SalesforceStage.ClosedWon]: closed(DealOutcome.Funded, null),
  [SalesforceStage.Passed]: closed(DealOutcome.Inactive, DealCloseReason.Other),
  [SalesforceStage.ClosedLost]: closed(DealOutcome.Lost, DealCloseReason.Other),
  // "Too Early" means too early-stage to lend to: too small, not bad timing.
  [SalesforceStage.TrackingEarly]: closed(DealOutcome.Passed, DealCloseReason.TooSmall),
  'Tracking - Too Early': closed(DealOutcome.Passed, DealCloseReason.TooSmall),
  [SalesforceStage.Rejected]: closed(DealOutcome.Inactive, DealCloseReason.Other),
  [SalesforceStage.Archived]: closed(DealOutcome.Inactive, DealCloseReason.Other),
  [SalesforceStage.Dead]: closed(DealOutcome.Inactive, DealCloseReason.Defunct),
}

/** Reason_Lost_Type__c, read back the same on Closed Lost and Passed. */
const SALESFORCE_LOST_TYPE_TO_DEAL: Record<string, SalesforceDealMapping> = {
  Competition: closed(DealOutcome.Lost, DealCloseReason.Competitor),
  Timing: closed(DealOutcome.Passed, DealCloseReason.Timing),
  'Financial Performance': closed(DealOutcome.Passed, DealCloseReason.Credit),
}

/** Null for an unmapped value: Salesforce admins can add picklist entries at any time. */
export const mapSalesforceStage = (
  salesforceStage: string | null | undefined,
  reasonLostType?: string | null,
): SalesforceDealMapping | null => {
  if (!salesforceStage) return null
  const takesLostType =
    salesforceStage === SalesforceStage.ClosedLost || salesforceStage === SalesforceStage.Passed
  if (takesLostType && reasonLostType) {
    return SALESFORCE_LOST_TYPE_TO_DEAL[reasonLostType] ?? SALESFORCE_STAGE_TO_DEAL[salesforceStage]
  }
  return SALESFORCE_STAGE_TO_DEAL[salesforceStage] ?? null
}

/** Lead statuses that mean an open deal. Intro or Mention only counts when a BD owns the Lead. */
export const LIVE_LEAD_STATUSES: string[] = Object.entries(SALESFORCE_STAGE_TO_DEAL)
  .filter(([, mapping]) => mapping.kind === 'stage' && mapping.stage === DealStage.Qualifying)
  .map(([salesforceStage]) => salesforceStage)

/** Write direction for an open stage. Qualifying lives on the Lead; OPS onward on the Opportunity. */
export const DEAL_STAGE_TO_SALESFORCE: Record<
  DealStage,
  { object: 'Lead' | 'Opportunity'; value: SalesforceStage }
> = {
  [DealStage.Qualifying]: { object: 'Lead', value: SalesforceStage.QualifyingCall },
  [DealStage.Ops]: { object: 'Opportunity', value: SalesforceStage.OpsCall },
  [DealStage.Committee]: { object: 'Opportunity', value: SalesforceStage.Submission },
  [DealStage.TermSheet]: { object: 'Opportunity', value: SalesforceStage.TermSheetNegotiation },
  [DealStage.DueDiligence]: { object: 'Opportunity', value: SalesforceStage.DueDiligence },
  [DealStage.Contract]: { object: 'Opportunity', value: SalesforceStage.ContractNegotiation },
  [DealStage.PreFunding]: { object: 'Opportunity', value: SalesforceStage.Funding },
}

export const requiresSalesforceOpportunity = (dealStage: DealStage): boolean =>
  DEAL_STAGE_TO_SALESFORCE[dealStage].object === 'Opportunity'

/** A Lead has no Lost status: the close's next step decides between parking and dropping it. */
const LOST_LEAD_STATUS: Record<DealNextStep, SalesforceStage> = {
  [DealNextStep.Tracking]: SalesforceStage.TrackingEarly,
  [DealNextStep.Rejected]: SalesforceStage.Archived,
  [DealNextStep.Defunct]: SalesforceStage.Dead,
}

/**
 * The nearest Salesforce values for a close, until cutover. Salesforce's Passed stage takes a
 * Reason Lost Type like Closed Lost does: with Timing or Financial Performance it is our Passed
 * (not now), without one our Inactive. The exact reason and note live in Atlas only.
 */
export const salesforceCloseFor = (
  outcome: DealOutcome,
  reason: DealCloseReason | null,
  nextStep: DealNextStep | null = null,
): { leadStatus: SalesforceStage; stageName: SalesforceStage; reasonLostType: string | null } => {
  switch (outcome) {
    case DealOutcome.Funded:
      return {
        leadStatus: SalesforceStage.Convert,
        stageName: SalesforceStage.ClosedWon,
        reasonLostType: null,
      }
    case DealOutcome.Inactive:
      return {
        leadStatus:
          reason === DealCloseReason.Defunct ? SalesforceStage.Dead : SalesforceStage.Rejected,
        stageName: SalesforceStage.Passed,
        reasonLostType: null,
      }
    case DealOutcome.Lost:
      return {
        leadStatus: LOST_LEAD_STATUS[nextStep ?? DealNextStep.Tracking],
        stageName: SalesforceStage.ClosedLost,
        reasonLostType: reason === DealCloseReason.Competitor ? 'Competition' : null,
      }
    case DealOutcome.Passed:
      return {
        leadStatus: SalesforceStage.TrackingEarly,
        stageName: SalesforceStage.Passed,
        // Financial Performance is Credit alone; Too small is a Lead-stage reason, so Timing.
        reasonLostType: reason === DealCloseReason.Credit ? 'Financial Performance' : 'Timing',
      }
  }
}
